/** 运营配置审计：解析后端 before/after JSON，生成运营可读文案 */

export interface OpsConfigSnapshot {
  platformRate?: number
  defaultOrgRate?: number
  transferFeeRate?: number
  transferFreeDaily?: number
  orgRates?: Record<string, number>
  dailyWithdrawLimit?: number
  etaKmRules?: { maxKm?: number | null; minutes?: number }[]
  cateringEnabled?: boolean
  failWhenDisabled?: boolean
  capacityCheckEnabled?: boolean
  minOnlineFree?: number
}

export interface OpsAuditChange {
  label: string
  from: string
  to: string
}

const FIELD_LABELS: Record<keyof OpsConfigSnapshot, string> = {
  platformRate: '平台费率',
  defaultOrgRate: '默认机构费率',
  transferFeeRate: '转单手续费率',
  transferFreeDaily: '日免费转单次数',
  orgRates: '机构费率覆盖',
  dailyWithdrawLimit: '日提现限额',
  etaKmRules: '公里时效档',
  cateringEnabled: '出餐进运力',
  failWhenDisabled: '关时拒绝出餐',
  capacityCheckEnabled: '容量软校验',
  minOnlineFree: '同城在线自由骑阈值'
}

const FIELD_ORDER: (keyof OpsConfigSnapshot)[] = [
  'platformRate',
  'defaultOrgRate',
  'transferFeeRate',
  'transferFreeDaily',
  'orgRates',
  'dailyWithdrawLimit',
  'etaKmRules',
  'cateringEnabled',
  'failWhenDisabled',
  'capacityCheckEnabled',
  'minOnlineFree'
]

function tryParseJson(text: string): OpsConfigSnapshot | undefined {
  try {
    return JSON.parse(text) as OpsConfigSnapshot
  } catch {
    return undefined
  }
}

/** 解析 detail：before={...} | after={...} */
export function parseOpsAuditDetail(detail?: string | null): {
  before?: OpsConfigSnapshot
  after?: OpsConfigSnapshot
} {
  if (!detail) return {}
  const marker = ' | after='
  const idx = detail.indexOf(marker)
  if (idx < 0) {
    const raw = detail.startsWith('before=') ? detail.slice(7) : detail
    return { before: tryParseJson(raw) }
  }
  const beforeStr = detail.slice('before='.length, idx)
  const afterStr = detail.slice(idx + marker.length)
  return {
    before: tryParseJson(beforeStr),
    after: tryParseJson(afterStr)
  }
}

function formatRate(value: unknown): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${(n * 100).toFixed(2)}%`
}

function formatOrgRates(value: unknown): string {
  if (!value || typeof value !== 'object') return '无'
  const entries = Object.entries(value as Record<string, number>)
  if (!entries.length) return '无'
  return entries.map(([orgId, rate]) => `${orgId} ${formatRate(rate)}`).join('；')
}

function formatBool(value: unknown): string {
  return value ? '开' : '关'
}

function formatFieldValue(key: keyof OpsConfigSnapshot, value: unknown): string {
  if (value === undefined || value === null) return '—'
  switch (key) {
    case 'platformRate':
    case 'defaultOrgRate':
    case 'transferFeeRate':
      return formatRate(value)
    case 'orgRates':
      return formatOrgRates(value)
    case 'cateringEnabled':
    case 'failWhenDisabled':
    case 'capacityCheckEnabled':
      return formatBool(value)
    case 'transferFreeDaily':
      return `${value} 次`
    case 'dailyWithdrawLimit':
      return `¥${value}`
    case 'etaKmRules':
      return formatEtaKmRules(value)
    case 'minOnlineFree':
      return `≥ ${value} 人`
    default:
      return String(value)
  }
}

function formatEtaKmRules(value: unknown): string {
  if (!Array.isArray(value) || !value.length) return '默认'
  return value
    .map((r: any) => {
      const min = r?.minutes ?? '—'
      return r?.maxKm == null ? `以上 ${min}分` : `≤${r.maxKm}km ${min}分`
    })
    .join('；')
}

function snapshotEqual(key: keyof OpsConfigSnapshot, a: unknown, b: unknown): boolean {
  if (key === 'orgRates' || key === 'etaKmRules') {
    return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {})
  }
  return a === b
}

/** 列出有变化的字段 */
export function listOpsAuditChanges(detail?: string | null): OpsAuditChange[] {
  const { before, after } = parseOpsAuditDetail(detail)
  if (!before && !after) {
    return detail ? [{ label: '明细', from: '—', to: detail }] : []
  }
  const changes: OpsAuditChange[] = []
  for (const key of FIELD_ORDER) {
    const fromVal = before?.[key]
    const toVal = after?.[key]
    if (snapshotEqual(key, fromVal, toVal)) continue
    changes.push({
      label: FIELD_LABELS[key],
      from: formatFieldValue(key, fromVal),
      to: formatFieldValue(key, toVal)
    })
  }
  return changes
}

/** 表格单行摘要：最多展示 2 项，其余用「等 N 项」 */
export function summarizeOpsAuditChanges(detail?: string | null, maxItems = 2): string {
  const changes = listOpsAuditChanges(detail)
  if (!changes.length) return '—'
  const head = changes
    .slice(0, maxItems)
    .map((c) => `${c.label}：${c.from} → ${c.to}`)
    .join('；')
  if (changes.length <= maxItems) return head
  return `${head}；等 ${changes.length} 项变更`
}

/** 从 actor 解析 admin 用户 id */
export function parseAdminActorId(actor?: string | null): number | null {
  const m = /^admin:(\d+)$/.exec(String(actor || '').trim())
  if (!m) return null
  const id = Number(m[1])
  return Number.isFinite(id) ? id : null
}

/** 操作者展示：优先昵称，其次用户名 */
export function formatOpsActor(
  actor?: string | null,
  nameMap?: Record<number, string>
): string {
  const raw = String(actor || '').trim()
  if (!raw) return '—'
  const id = parseAdminActorId(raw)
  if (id != null && nameMap?.[id]) return nameMap[id]
  if (raw === 'platform') return '平台'
  if (raw === 'system') return '系统'
  return raw
}
